import { useState } from "react";
import useDataset from "datasets/hooks/useDataset";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import { Loader2, Pencil, Search, Trash2 } from "lucide-react";
import {
  SKILL_EDITOR_NAME_MAX_LENGTH,
  SkillEditorMode,
  type SkillFormData,
} from "~/components/assistant/SkillEditorForm";
import SkillEditorModal from "~/components/assistant/SkillEditorModal";
import SkillPickerModal from "~/components/assistant/SkillPickerModal";
import type { LibrarySkill } from "~/components/assistant/hooks/useSkillLibrary";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import useErrorToast from "~/contexts/error-toast";
import { useDatasetAttachedSkills } from "~/pages/datasets/components/assistant/hooks/useDatasetAttachedSkills";
import { getSkill, invalidateAllSkillLists, updateSkill, type Skill } from "~/services/skills";
import rumClient from "~/utils/monitoring";

type SkillAction =
  | { mode: "none" }
  | { mode: "picker" }
  | { mode: "creating"; initialData?: Partial<SkillFormData>; sourceName?: string }
  | { mode: "editing"; skill: Skill };

const DatasetSkillsTab = () => {
  const { data: dataset } = useDataset();
  const { showError } = useErrorToast();
  const isOwner = dataset?.access_level === DatasetAccessLevel.OWNER;

  const [isDetaching, setIsDetaching] = useState(false);
  const [editorLoading, setEditorLoading] = useState(false);
  const [skillAction, setSkillAction] = useState<SkillAction>({ mode: "none" });
  const [detachingSkillId, setDetachingSkillId] = useState<string | null>(null);

  const datasetId = dataset?.id;
  const {
    skills,
    isLoading: loading,
    error,
    createSkill,
    detach,
  } = useDatasetAttachedSkills(datasetId);
  const fetchError = !!error;

  const handleAddSkill = () => {
    setSkillAction({ mode: "picker" });
  };

  const handlePickFromLibrary = async (skill: LibrarySkill) => {
    try {
      const sourceSkill = await getSkill(skill.id);
      setSkillAction({
        mode: "creating",
        sourceName: sourceSkill.name,
        initialData: {
          name: `${sourceSkill.name}-copy`.slice(0, SKILL_EDITOR_NAME_MAX_LENGTH),
          description: sourceSkill.description,
          content: sourceSkill.content,
        },
      });
    } catch (e) {
      console.error(e);
      showError("Unable to load skill from the library");
    }
  };

  const handleCreateNew = () => setSkillAction({ mode: "creating" });

  const handleEditSkill = async (skillId: string) => {
    try {
      const skill = await getSkill(skillId);
      setSkillAction({ mode: "editing", skill });
    } catch (e) {
      console.error(e);
      showError("Unable to load skill for editing");
    }
  };

  const handleEditorSubmit = async (data: SkillFormData) => {
    if ((skillAction.mode !== "editing" && skillAction.mode !== "creating") || !datasetId) {
      return;
    }
    setEditorLoading(true);
    try {
      if (skillAction.mode === "editing") {
        await updateSkill(skillAction.skill.id, {
          description: data.description,
          content: data.content,
        });
        await invalidateAllSkillLists();
      } else {
        await createSkill(data);
      }
      setSkillAction({ mode: "none" });
    } catch (e) {
      console.error(e);
      rumClient?.recordError(e);
      showError(e instanceof Error ? e.message : "Unable to save skill");
    } finally {
      setEditorLoading(false);
    }
  };

  const editorInitialData =
    skillAction.mode === "editing"
      ? {
          name: skillAction.skill.name,
          description: skillAction.skill.description ?? "",
          content: skillAction.skill.content,
        }
      : skillAction.mode === "creating"
        ? skillAction.initialData
        : undefined;

  const handleDetachSkill = async () => {
    if (!detachingSkillId) return;
    setIsDetaching(true);
    try {
      await detach(detachingSkillId);
      setDetachingSkillId(null);
    } catch (e) {
      console.error(e);
      rumClient?.recordError(e);
      showError("Unable to remove skill from dataset");
    } finally {
      setIsDetaching(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (fetchError) {
    return (
      <p className="text-sm text-destructive">
        Failed to load dataset skills. Please try refreshing the page.
      </p>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-4">
        <p className="text-sm text-muted-foreground">
          Skills provide domain-specific instructions and workflows for AI Analysis conversations on
          this dataset.
        </p>

        {skills.length > 0 && (
          <div className="flex flex-col gap-1">
            {skills.map((skill) => (
              <div key={skill.id} className="flex items-center justify-between py-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{skill.name}</p>
                  {skill.description && (
                    <p className="truncate text-[13px] text-muted-foreground">
                      {skill.description}
                    </p>
                  )}
                </div>

                {isOwner && (
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-8"
                      onClick={() => handleEditSkill(skill.id)}
                      aria-label={`Edit ${skill.name}`}
                    >
                      <Pencil className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-8"
                      onClick={() => setDetachingSkillId(skill.id)}
                      aria-label={`Remove ${skill.name} from dataset`}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {isOwner && (
          <Button
            variant="secondary"
            size="sm"
            className="self-start bg-gray-200 hover:bg-gray-300"
            onClick={handleAddSkill}
            aria-label="Browse skills"
          >
            <Search className="size-4" />
            Browse Skills
          </Button>
        )}
      </div>

      <AlertDialog
        open={!!detachingSkillId}
        onOpenChange={(open) => !open && !isDetaching && setDetachingSkillId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove skill from dataset?</AlertDialogTitle>
            <AlertDialogDescription>
              &ldquo;{skills.find((s) => s.id === detachingSkillId)?.name}&rdquo; will no longer be
              available for AI Analysis on this dataset. The skill remains in your library and can
              be re-attached at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDetaching}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDetachSkill}
              disabled={isDetaching}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDetaching ? "Removing…" : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SkillPickerModal
        open={skillAction.mode === "picker"}
        onClose={() => setSkillAction({ mode: "none" })}
        onPick={handlePickFromLibrary}
        onCreateNew={handleCreateNew}
      />

      <SkillEditorModal
        open={skillAction.mode === "creating" || skillAction.mode === "editing"}
        onClose={() => setSkillAction({ mode: "none" })}
        onSubmit={handleEditorSubmit}
        loading={editorLoading}
        mode={skillAction.mode === "editing" ? SkillEditorMode.EDIT : SkillEditorMode.CREATE}
        initialData={editorInitialData}
        duplicateSourceName={skillAction.mode === "creating" ? skillAction.sourceName : undefined}
        description={
          skillAction.mode === "creating" && skillAction.sourceName
            ? `Edit before saving — original is unchanged. This copy will be attached to "${dataset?.title}".`
            : `This skill will be attached to "${dataset?.title}".`
        }
      />
    </>
  );
};

export default DatasetSkillsTab;
